class Wishlist {
  constructor() {
    this.customerId = this.getCustomerId();
    this.wishlistedVariants = new Set(); // Track wishlisted variant IDs
    this.init();
  }

  getCustomerId() {
    // Get from Liquid template
    return window.wishlistCustomerId || null;
  }

  async init() {
    // Load existing wishlist on page load
    await this.loadWishlist();
    
    // Set up event listeners
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.wishlist-button');
      if (!button) return;

      e.preventDefault();
      
      // Try to get variant ID from the product form's hidden input first
      const variantIdInput = button.closest('form, .product-form, product-form')?.querySelector('.product-variant-id');
      const variantId = variantIdInput ? variantIdInput.value : button.dataset.variantId;
      const productId = button.dataset.productId;
      const isActive = button.classList.contains('active');

      if (isActive) {
        this.remove(variantId);
      } else {
        this.add(productId, variantId);
      }
    });
  }

  async loadWishlist() {
    if (!this.customerId) return;

    try {
      const response = await fetch(`/apps/wishlist/get?customer_id=${this.customerId}`);
      if (response.ok) {
        const data = await response.json();
        
        // Store variant IDs in Set
        if (data.items && Array.isArray(data.items)) {
          data.items.forEach(item => {
            this.wishlistedVariants.add(item.variantId.toString());
          });
        }
        
        // Update UI for all buttons
        this.updateAllButtons();
      }
    } catch (error) {
      console.error('Error loading wishlist:', error);
    }
  }

  updateAllButtons() {
    // Update all wishlist buttons on the page
    document.querySelectorAll('.wishlist-button').forEach(button => {
      const variantId = button.dataset.variantId;
      const isWishlisted = this.wishlistedVariants.has(variantId);
      button.classList.toggle('active', isWishlisted);
      
      // Update button text if it exists
      const buttonText = button.querySelector('.wishlist-button__text');
      if (buttonText) {
        buttonText.textContent = isWishlisted ? '\u00A0 In Wishlist' : '\u00A0 Add to Wishlist';
      }
    });
  }

  async add(productId, variantId) {
    if (!this.customerId) {
      window.location.href = '/account/login';
      return;
    }

    // Validate all required fields before sending
    if (!productId || !variantId) {
      console.error('Missing productId or variantId:', { productId, variantId });
      this.showNotification('Error: Missing product information');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('customer_id', this.customerId);
      formData.append('product_id', productId);
      formData.append('variant_id', variantId);

      console.log('Sending wishlist add request:', {
        customer_id: this.customerId,
        product_id: productId,
        variant_id: variantId
      });

      const response = await fetch('/apps/wishlist/add', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        this.wishlistedVariants.add(variantId); // Add to Set
        this.updateUI(variantId, true);
        this.showNotification('Added to wishlist!');
      } else {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        this.showNotification(`Error: ${errorData.error || 'Failed to add to wishlist'}`);
      }
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      this.showNotification('Error: Failed to add to wishlist');
    }
  }

  async remove(variantId) {
    try {
      const formData = new FormData();
      formData.append('customer_id', this.customerId);
      formData.append('variant_id', variantId);

      const response = await fetch('/apps/wishlist/remove', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        this.wishlistedVariants.delete(variantId); // Remove from Set
        this.updateUI(variantId, false);
        this.showNotification('Removed from wishlist');
      }
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  }

  updateUI(variantId, isAdded) {
    const buttons = document.querySelectorAll(`[data-variant-id="${variantId}"]`);
    buttons.forEach(button => {
      button.classList.toggle('active', isAdded);
      
      // Update button text if it exists
      const buttonText = button.querySelector('.wishlist-button__text');
      if (buttonText) {
        buttonText.textContent = isAdded ? '\u00A0 In Wishlist' : '\u00A0 Add to Wishlist';
      }
    });
  }

  showNotification(message) {
    // Simple notification (customize as needed)
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => notification.remove(), 3000);
  }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
  new Wishlist();
});
